/**
 * Authentication context for the SIGB application.
 * Provides user state and authentication methods to the entire app.
 */

import { createContext, useContext, useEffect, useState, useCallback, type ReactNode } from "react";
import type { CurrentUser, UserRole } from "@/api/types";
import * as api from "@/api/client";

interface AuthState {
  user: CurrentUser | null;
  isLoading: boolean;
  isAuthenticated: boolean;
}

interface AuthContextValue extends AuthState {
  login: (email: string, password: string) => Promise<void>;
  logout: () => void;
  refreshUser: () => Promise<void>;
  hasRole: (role: UserRole) => boolean;
  hasAnyRole: (roles: UserRole[]) => boolean;
  isSuperAdmin: boolean;
  isAdmin: boolean;
}

const AuthContext = createContext<AuthContextValue | null>(null);

interface AuthProviderProps {
  children: ReactNode;
}

export function AuthProvider({ children }: AuthProviderProps) {
  const [state, setState] = useState<AuthState>({
    user: null,
    isLoading: true,
    isAuthenticated: false,
  });

  /**
   * Fetch the current user from the API.
   */
  const fetchUser = useCallback(async () => {
    try {
      const user = await api.getCurrentUser();
      setState({
        user,
        isLoading: false,
        isAuthenticated: true,
      });
    } catch {
      setState({
        user: null,
        isLoading: false,
        isAuthenticated: false,
      });
    }
  }, []);

  /**
   * Login with email and password.
   */
  const login = useCallback(async (email: string, password: string) => {
    setState((prev) => ({ ...prev, isLoading: true }));

    try {
      await api.login(email, password);
      // Fetch full user data after login to get complete location info
      const fullUser = await api.getCurrentUser();
      setState({
        user: fullUser,
        isLoading: false,
        isAuthenticated: true,
      });
    } catch (error) {
      setState({
        user: null,
        isLoading: false,
        isAuthenticated: false,
      });
      throw error;
    }
  }, []);

  /**
   * Logout the current user.
   */
  const logout = useCallback(() => {
    api.logout();
    setState({
      user: null,
      isLoading: false,
      isAuthenticated: false,
    });
  }, []);

  /**
   * Refresh user data from the API.
   */
  const refreshUser = useCallback(async () => {
    if (!api.isAuthenticated()) {
      return;
    }
    await fetchUser();
  }, [fetchUser]);

  /**
   * Check if the user has a specific role.
   */
  const hasRole = useCallback(
    (role: UserRole): boolean => {
      if (!state.user) return false;
      return state.user.roles.includes(role);
    },
    [state.user]
  );

  /**
   * Check if the user has any of the specified roles.
   */
  const hasAnyRole = useCallback(
    (roles: UserRole[]): boolean => {
      if (!state.user) return false;
      const userRoles = state.user.roles;
      return roles.some((role) => userRoles.includes(role));
    },
    [state.user]
  );

  /**
   * Check if user is super admin.
   */
  const isSuperAdmin = hasRole("ROLE_SUPER_ADMIN");

  /**
   * Check if user is admin (includes super admin).
   */
  const isAdmin = hasAnyRole(["ROLE_ADMIN", "ROLE_SUPER_ADMIN"]);

  // Initialize auth state on mount
  useEffect(() => {
    if (api.isAuthenticated()) {
      void fetchUser();
    } else {
      setState({
        user: null,
        isLoading: false,
        isAuthenticated: false,
      });
    }
  }, [fetchUser]);

  // Listen for auth events
  useEffect(() => {
    const handleUnauthorized = () => {
      setState({
        user: null,
        isLoading: false,
        isAuthenticated: false,
      });
    };

    const handleLogout = () => {
      setState({
        user: null,
        isLoading: false,
        isAuthenticated: false,
      });
    };

    window.addEventListener("auth:unauthorized", handleUnauthorized);
    window.addEventListener("auth:logout", handleLogout);

    return () => {
      window.removeEventListener("auth:unauthorized", handleUnauthorized);
      window.removeEventListener("auth:logout", handleLogout);
    };
  }, []);

  const value: AuthContextValue = {
    ...state,
    login,
    logout,
    refreshUser,
    hasRole,
    hasAnyRole,
    isSuperAdmin,
    isAdmin,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

/**
 * Hook to access the auth context.
 * Must be used within an AuthProvider.
 */
export function useAuth(): AuthContextValue {
  const context = useContext(AuthContext);

  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }

  return context;
}
